import type { AuthFormProps, Component } from './types';
import type { ReCAPTCHAHandle } from '@steamer-academy/react-google-recaptcha';

import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { RESULT_VARIANT_ERROR } from '@sgcmpteltd/uikit-react/components/result';
import { useMutation } from '@tanstack/react-query';

import { LoginContext } from './context';
import { LoginScreenEnum } from './enums';
import { requestDataSchema } from './validation';
import { CustomReCAPTCHA } from '@/components/common/recaptcha';
import {
  LoginResponseCode,
  OtpResendResponseCode,
  OtpResponseCode,
} from '@/enums/response-message/auth';

import { ACTION_STATUS_INITIAL } from '@/actions';
import { submitLoginRequest } from '@/actions/auth/login/action';
import { resendLoginOtpAction } from '@/actions/auth/resend-login-otp/action';
import { verifyLoginOtpAction } from '@/actions/auth/verify-login-otp/action';
import {
  ACTION_STATUS_ERROR,
  ACTION_STATUS_SUCCESS,
  ACTION_STATUS_VALIDATION_ERROR,
} from '@/actions/constants';
import type { OtpCode } from '@/hooks/use-otp';
import { useOtp } from '@/hooks/use-otp';
import { usePageLoader } from '@/hooks/use-page-loader';
import { useResponseDialog } from '@/hooks/use-response-dialog';
import { useRhfSetError } from '@/hooks/use-rhf-set-error';
import { toast } from '@/hooks/use-toast';

export const LoginProvider: Component = ({
  children,
  initScreen = LoginScreenEnum.login,
  session,
}) => {
  const recaptchaRef = useRef<ReCAPTCHAHandle>(null);
  const [screen, setScreen] = useState<LoginScreenEnum>(initScreen);
  const { addDialog } = useResponseDialog();
  const { setIsPageLoading } = usePageLoader();
  const defaultEmail
    = typeof window !== 'undefined'
      ? (localStorage.getItem('last_login_email') ?? '')
      : '';
  const formValue = useForm<AuthFormProps>({
    defaultValues: {
      email: defaultEmail,
      key: '',
      mobile: '',
      otp: null,
      password: '',
      reset_after: null,
      skip_next_time: 1,
    },
    resolver: standardSchemaResolver(requestDataSchema),
  });

  const {
    clearErrors,
    formState: { errors },
    getValues,
    setError,
    setValue,
  } = formValue;
  const { setMultipleLaravelFormatError } = useRhfSetError<AuthFormProps>({
    setError,
  });

  const {
    hasOtpRetryAttempts,
    nextOtpResendableRemainSeconds,
    onNextOtpResendableTimeChange,
    onOtpCodeChange,
    onOtpCodeExpiredTimeChange,
    onOtpCodeRetryAttemptsChange,
    onOtpRetryAttemptsChange,
    otpCode,
  } = useOtp({
    initialOtpResendableTime: 30,
    onOtpVerify: async (newOtpCode) => {
      const data = {
        key: getValues('key'),
        otp: +newOtpCode.join(''),
        skip_next_time: getValues('skip_next_time'),
      };
      // eslint-disable-next-line ts/no-use-before-define
      verifyLoginOtp(data);
    },
  });

  const { isPending: isSubmittingLogin, mutate: submitLogin } = useMutation({
    mutationFn: submitLoginRequest,
    onSettled: () => {
      setIsPageLoading(false);
    },
    onSuccess(response) {
      recaptchaRef.current?.reset();
      setIsPageLoading(false);

      if (response.status === ACTION_STATUS_INITIAL) {
        return;
      }

      if (response.status === ACTION_STATUS_VALIDATION_ERROR) {
        toast({
          description:
            'Please ensure that all compulsory fields are filled out correctly.',
          title: 'Error',
          variant: 'destructive',
        });

        if (response.errors) {
          Object.entries(response.errors).forEach(([key, value], index) => {
            const errorKey = key as keyof typeof response.errors;
            const errorValue = value && value.length !== 0 ? value[0] : null;

            if (errorValue) {
              setError(
                errorKey,
                {
                  message: errorValue,
                  type: 'custom',
                },
                {
                  shouldFocus: index === 0,
                },
              );
            }
          });
        }
      } else if (response.status === ACTION_STATUS_ERROR) {
        addDialog({
          message: response.error,
          title: 'Error(s) Found!',
          type: RESULT_VARIANT_ERROR,
        });
      } else if (
        response.status === ACTION_STATUS_SUCCESS
        && response.data.message === LoginResponseCode.login_verification_required
      ) {
        const countdown
          = Math.floor(Date.now() / 1000) + response.data.renew_after;
        const otpCodeExpiredTime = Math.floor(Date.now() / 1000) + 120;

        setValue('key', response.data.key);
        setValue('mobile', response.data.mobile_number);
        setScreen(LoginScreenEnum.otp);

        onOtpCodeRetryAttemptsChange(3);
        onOtpRetryAttemptsChange(3);
        onNextOtpResendableTimeChange(countdown);
        onOtpCodeExpiredTimeChange(otpCodeExpiredTime);
      } else if (
        response.status === ACTION_STATUS_SUCCESS
        && response.data.message === LoginResponseCode.ok
      ) {
        // Persist last used email before redirect
        const emailValue = getValues('email');
        if (emailValue) {
          localStorage.setItem('last_login_email', emailValue);
        }

        // Clear router state contamination by forcing a full page reload
        // This prevents Next.js from maintaining the logout route in its internal state
        window.location.href = response.data.redirectUrl;
      }
    },
  });

  const { isPending: isVerifyingOtp, mutate: verifyLoginOtp } = useMutation({
    mutationFn: verifyLoginOtpAction,
    onSuccess: (response) => {
      recaptchaRef.current?.reset();

      if (response.status === ACTION_STATUS_INITIAL) {
        return;
      }

      if (response.status === ACTION_STATUS_SUCCESS) {
        onOtpCodeRetryAttemptsChange(0);
        // Persist last used email before redirect
        const emailValue = getValues('email');
        if (emailValue) {
          localStorage.setItem('last_login_email', emailValue);
        }

        // Clear router state contamination by forcing a full page reload
        // This prevents Next.js from maintaining the logout route in its internal state
        window.location.href = response.data.redirectUrl;
      } else if (response.status === ACTION_STATUS_VALIDATION_ERROR) {
        const errors = response.errors;
        const isOtpCodeInvalid
          = errors.otp?.some(message =>
            Object.values(OtpResponseCode).includes(message as OtpResponseCode),
          ) ?? false;

        if (isOtpCodeInvalid && errors.otp?.[0]) {
          let message = 'Something went wrong';
          switch (errors.otp[0]) {
            case OtpResponseCode.ATTEMPT_ERROR:
              message = 'Invalid OTP request. Please try again.';
              break;
            case OtpResponseCode.ATTEMPT_EXCEEDED_LIMIT:
              message = 'OTP was incorrect. Maximum tries reached.';
              onOtpCodeRetryAttemptsChange(0);
              break;
            case OtpResponseCode.ATTEMPT_EXPIRED:
              message = 'OTP was expired. Please request a new OTP.';
              break;
            case OtpResponseCode.ATTEMPT_FAILED:
              message = 'OTP was incorrect';
              break;
            case OtpResponseCode.ATTEMPT_NOT_FOUND:
              message = 'OTP request was not found';
              break;
          }
          setError('otp', { message });
        } else {
          setMultipleLaravelFormatError(response.errors);
        }

        onOtpCodeChange([undefined, undefined, undefined, undefined]);
      } else {
        const message = response.error ?? 'Otp request not found';
        setError('otp', { message });
        onOtpCodeChange([undefined, undefined, undefined, undefined]);
      }
    },
  });

  const { isPending: isResendingOtp, mutate: resendLoginOtp } = useMutation({
    mutationFn: resendLoginOtpAction,
    onSuccess: (response) => {
      if (response.status === ACTION_STATUS_INITIAL) {
        return;
      }
      clearErrors('otp');
      onOtpCodeChange([undefined, undefined, undefined, undefined]);
      setValue('otp', null);
      if (response.status === ACTION_STATUS_SUCCESS) {
        setValue('key', response.data.key);

        const countdown
          = Math.floor(Date.now() / 1000) + response.data.renew_after;
        const otpCodeExpiredTime = Math.floor(Date.now() / 1000) + 120;

        onOtpCodeRetryAttemptsChange(3);
        onNextOtpResendableTimeChange(countdown);
        onOtpCodeExpiredTimeChange(otpCodeExpiredTime);
      } else if (response.status === ACTION_STATUS_ERROR) {
        let message = 'Something went wrong';
        switch (response.error) {
          case OtpResendResponseCode.ATTEMPT_NOT_FOUND:
          case OtpResendResponseCode.RESEND_ERROR:
            message
              = 'Unable to resend OTP. Please refresh the page and try again.';
            break;
          case OtpResendResponseCode.RESEND_COOLDOWN:
            message = 'Please wait before requesting a new OTP.';
            break;
          case OtpResendResponseCode.RESEND_MAX_LIMIT:
            message
              = 'Maximum OTP resend limit reached. Please try again later.';
            break;
        }

        addDialog({
          message,
          title: 'OTP Resend Failed',
          type: RESULT_VARIANT_ERROR,
        });
      }
    },
  });

  const onChangeOtp = async (newOtpCode: OtpCode) => {
    onOtpCodeChange(newOtpCode);
    clearErrors('otp');
  };

  const onResendOtp = () => {
    resendLoginOtp({ key: getValues('key') });
  };

  return (
    <LoginContext
      value={{
        hasOtpRetryAttempts,
        isOtpCodeInvalid: Boolean(errors.otp),
        isSubmittingLogin,
        isVerifyingOtp: isVerifyingOtp || isResendingOtp,
        nextOtpResendableRemainSeconds,
        onChangeOtp,
        onResendOtp,
        otpCode,
        recaptchaRef,
        screen,
        session,
        setScreen,
        submitLogin,
      }}
    >
      <FormProvider {...formValue}>
        {children}
        <CustomReCAPTCHA
          onChange={token => setValue('recaptcha_token', token ?? '')}
          ref={recaptchaRef}
        />
      </FormProvider>
    </LoginContext>
  );
};
